import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import {
  doDateRangesOverlap,
  formatDateRangeUTC,
  formatDateUTC,
  formatEventDateUTC,
  formatTimeUTC,
} from '@sola/utils/date.ts';

// ── Types ────────────────────────────────────────────────────────────────────

export interface VolunteerSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description: string | null;
  remainingCapacity: number;
}

/** Maps contactId → array of slotIds they are assigned to. */
export type ShiftAssignments = Record<string, string[]>;

// ── Display formatting ───────────────────────────────────────────────────────

/**
 * Format a volunteer event's date range for display.
 * Uses UTC to prevent hydration mismatches.
 */
export const formatEventDateRange = (
  volunteer: Pick<
    VolunteerLocationItemDto,
    'startDate' | 'endDate' | 'tourEvents'
  >,
  isTour: boolean,
  variant: 'default' | 'card' | 'event' = 'default',
): string => {
  const startDate = new Date(volunteer.startDate);

  // Event detail page:
  // Single day: "Thu • March 12, 2026 • 7:00 PM"
  // Multi-day:  "Thu, March 12 – Sat, March 14, 2026"
  if (variant === 'event' && !isTour) {
    return formatEventDateUTC(volunteer.startDate, volunteer.endDate);
  }

  // Event card: "Sat, Mar 15 • 7:00 PM"
  if (variant === 'card' && !isTour) {
    const shortDate = formatDateUTC(startDate, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${shortDate} \u2022 ${formatTimeUTC(startDate)}`;
  }

  // Default: date range
  const tourEvents = volunteer.tourEvents ?? [];
  const lastEndDate = isTour
    ? tourEvents.reduce(
        (max, e) => (e?.endDate && e.endDate > max ? e.endDate : max),
        volunteer.endDate,
      )
    : volunteer.endDate;

  return formatDateRangeUTC(volunteer.startDate, lastEndDate, {
    monthOnly: isTour,
  });
};

/**
 * Build a location display string, optionally showing tour location count.
 */
export const formatLocationDisplay = (
  volunteer: Pick<VolunteerLocationItemDto, 'city' | 'state' | 'tourEvents'>,
  isTour: boolean,
  isTourList: boolean,
): string => {
  const tourEvents = volunteer.tourEvents ?? [];
  const uniqueTourLocations = isTour
    ? [...new Set(tourEvents.map((e) => `${e.city}, ${e.state}`))]
    : [];
  const otherLocationCount = uniqueTourLocations.length - 1;

  if (isTour && !isTourList && otherLocationCount > 0) {
    return `${volunteer.city}, ${volunteer.state} + ${otherLocationCount} other location${otherLocationCount > 1 ? 's' : ''}`;
  }

  return `${volunteer.city}, ${volunteer.state}`;
};

// ── Role matching & filtering ────────────────────────────────────────────────

/**
 * Normalize a role name for fuzzy matching.
 * Lowercases, strips hyphens, normalizes compound words, and collapses whitespace.
 */
export const normalizeRoleName = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replaceAll('-', ' ')
    .replaceAll('setup', 'set up')
    .replaceAll('teardown', 'tear down')
    .replaceAll(/\s+/g, ' ');

/**
 * Match a content item title to a role slot count using fuzzy name matching.
 * Falls back to prefix matching (e.g. "Event Volunteer" matches "Event Facilitator").
 */
export const findMatchingRole = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number } | undefined => {
  const titleNorm = normalizeRoleName(title);

  // Direct or substring match
  const direct = roleCounts.find((rc) => {
    const roleNorm = normalizeRoleName(rc.role);
    return (
      roleNorm === titleNorm ||
      roleNorm.includes(titleNorm) ||
      titleNorm.includes(roleNorm)
    );
  });
  if (direct) {
    return direct;
  }

  // Prefix fallback: if title starts with "event", match any role starting with "event"
  const titlePrefix = titleNorm.split(' ')[0];
  if (titlePrefix === 'event') {
    return roleCounts.find((rc) =>
      normalizeRoleName(rc.role).startsWith('event'),
    );
  }

  return undefined;
};

/**
 * Roles that should only be shown when they have available slots.
 * All other roles are always visible regardless of slot availability.
 */
const conditionalRoles = new Set([
  'handout volunteer',
  'compassion experience volunteer',
  'vr experience volunteer',
  'volunteer entrance host',
]);

/**
 * Determine if a role item should be visible.
 * Conditional roles are hidden when they have no open slots.
 */
export const shouldShowRole = (
  title: string,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
): boolean => {
  const titleNorm = normalizeRoleName(title);
  if (!conditionalRoles.has(titleNorm)) {
    return true;
  }
  if (!roleCounts) {
    return true; // No data yet — show all until we know
  }
  const match = findMatchingRole(title, roleCounts);
  return !!match && match.openSlots > 0;
};

/**
 * Check if a content item title matches any role in a list of role names.
 * Uses the same fuzzy matching as findMatchingRole (normalize + includes + event prefix).
 */
export const hasMatchingRole = (title: string, roles: string[]): boolean => {
  const asCounts = roles.map((r) => ({ role: r, openSlots: 0, totalSlots: 0 }));
  return !!findMatchingRole(title, asCounts);
};

/**
 * Enrich role content items with live slot count badges, or filter by indexed roles.
 *
 * When `roleCounts` is available (live data loaded), filters to roles with open slots
 * and attaches a badge showing the count.
 *
 * When `roleCounts` is not yet available, falls back to filtering items by the
 * indexed `roles` array (no badge).
 */
export const buildFilteredRoleItems = <T extends { title?: string | null }>(
  items: T[] | undefined,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
  indexedRoles: string[],
  formatBadgeLabel: (openSlots: number) => string,
): (T & { badge?: { label: string; color: string; size: string } })[] => {
  if (!items) {
    return [];
  }

  if (roleCounts) {
    return items
      .filter((item) => shouldShowRole(item.title ?? '', roleCounts))
      .map((item) => {
        const matchingRole = findMatchingRole(item.title ?? '', roleCounts);
        if (!matchingRole) {
          return null;
        }
        return {
          ...item,
          badge: {
            label: formatBadgeLabel(matchingRole.openSlots),
            color: 'brand',
            size: 'sm',
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  return items
    .filter((item) => hasMatchingRole(item.title ?? '', indexedRoles))
    .map((item) => ({ ...item }));
};

// ── Slot grouping & deduplication ────────────────────────────────────────────

interface RawSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description?: string | null;
}

/** Slots with the same key represent interchangeable positions. */
export const getSlotGroupKey = (slot: {
  description?: string | null;
  startDate: string;
  endDate: string;
}): string =>
  `${slot.description ?? 'General'}__${slot.startDate}__${slot.endDate}`;

/**
 * Extract the date portion from an ISO-ish datetime string (e.g. "2026-09-26").
 * Works with both naive local times and UTC strings.
 */
export const getSlotDateKey = (isoString: string): string =>
  isoString.slice(0, 10);

/**
 * Collapses available slots with the same role + time into grouped entries.
 * `remainingCapacity` reflects how many individual slots remain.
 */
export const deduplicateSlots = (rawSlots: RawSlot[]): VolunteerSlot[] => {
  const available = rawSlots.filter((s) => s.status === 'Available');

  const groupMap = new Map<string, { ids: string[]; slot: RawSlot }>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const existing = groupMap.get(key);
    if (existing) {
      existing.ids.push(slot.id);
    } else {
      groupMap.set(key, { ids: [slot.id], slot });
    }
  }

  return [...groupMap.values()].map(({ ids, slot }) => ({
    id: ids[0],
    eventLocationId: slot.eventLocationId,
    startDate: slot.startDate,
    endDate: slot.endDate,
    status: slot.status,
    description: slot.description ?? null,
    remainingCapacity: ids.length,
  }));
};

/** Maps each slot group key to all available raw slot IDs in that group. */
export const buildSlotGroupIds = (
  rawSlots: RawSlot[],
): Map<string, string[]> => {
  const available = rawSlots.filter((s) => s.status === 'Available');
  const pool = new Map<string, string[]>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const ids = pool.get(key) ?? [];
    ids.push(slot.id);
    pool.set(key, ids);
  }
  return pool;
};

/** Groups slots by role label, sorted by start time within each group. */
export const groupSlotsByRole = (
  slots: VolunteerSlot[],
  fallbackLabel: string,
): Array<[string, VolunteerSlot[]]> => {
  const byRole: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const key = slot.description ?? fallbackLabel;
    const group = byRole[key] ?? [];
    group.push(slot);
    byRole[key] = group;
  }
  for (const group of Object.values(byRole)) {
    group.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
  }
  return Object.entries(byRole);
};

/**
 * Group a sorted array of slots by their date. Returns entries in chronological order.
 * Only meaningful for multi-day events — single-day events return a single group.
 */
export const groupSlotsByDate = (
  slots: VolunteerSlot[],
): Array<[string, VolunteerSlot[]]> => {
  const byDate: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const dateKey = getSlotDateKey(slot.startDate);
    const group = byDate[dateKey] ?? [];
    group.push(slot);
    byDate[dateKey] = group;
  }
  return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
};

// ── Conflict detection & capacity ────────────────────────────────────────────

/** Checks whether a contact has a time conflict with the given slot. */
export const hasSlotTimeConflict = (
  contactId: string,
  targetSlot: VolunteerSlot,
  allSlots: VolunteerSlot[],
  assignments: ShiftAssignments,
  slotGroupIds: Map<string, string[]>,
): boolean => {
  const contactAssignedIds = assignments[contactId] ?? [];
  const assignedSlots = allSlots.filter((s) => {
    const groupIds = slotGroupIds.get(getSlotGroupKey(s)) ?? [];
    return (
      s.id !== targetSlot.id &&
      contactAssignedIds.some((id) => groupIds.includes(id))
    );
  });
  return assignedSlots.some((s) =>
    doDateRangesOverlap(
      s.startDate,
      s.endDate,
      targetSlot.startDate,
      targetSlot.endDate,
    ),
  );
};

/** Checks whether a slot's capacity is exhausted for a specific contact. */
export const isSlotAtCapacity = (
  contactId: string,
  slot: VolunteerSlot,
  pool: string[],
  assignments: ShiftAssignments,
): boolean => {
  const othersAssigned = new Set(
    Object.entries(assignments)
      .filter(([cId]) => cId !== contactId)
      .flatMap(([, ids]) => ids),
  );
  const takenByOthers = pool.filter((id) => othersAssigned.has(id)).length;
  return takenByOthers >= slot.remainingCapacity;
};
